import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { completePopupRelay, rememberPopupRelay } from "../../utils/shoplineEmbedded";

const API_BASE = (process.env.REACT_APP_SERVER_ENDPOINT || "").replace(/\/+$/, "");
const SOCIAL = ["linkedin", "facebook", "twitter", "youtube"];

const shell = (children: React.ReactNode) => (
  <Box sx={{
    minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", px: 3,
    background: "#0F1117", color: "#fff", textAlign: "center",
  }}>
    <Box sx={{ maxWidth: 360 }}>{children}</Box>
  </Box>
);

/**
 * /shopline/popup — first page of the popup opened from the embedded app.
 * Remembers the relay id, then leaves for Stripe (?to=…) or a social login (?social=…&code=…).
 */
export const ShoplinePopup: React.FC = () => {
  const [error, setError] = useState("");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const relay = p.get("relay") || "";
    const to = p.get("to") || "";
    const social = p.get("social") || "";
    const code = p.get("code") || "";
    if (!relay) { setError("This window was opened incorrectly."); return; }
    rememberPopupRelay(relay);

    if (social && SOCIAL.includes(social) && code) {
      window.location.replace(
        `${API_BASE}/api/integrations/shopline/embedded/social/${social}?code=${encodeURIComponent(code)}`
      );
      return;
    }
    try {
      const u = new URL(to);
      if (u.protocol !== "https:" && u.protocol !== "http:") throw new Error("bad url");
      window.location.replace(u.href);
    } catch {
      setError("This window was opened incorrectly.");
    }
  }, []);

  return shell(error
    ? <Typography>{error} You can close it.</Typography>
    : <><CircularProgress size={26} sx={{ color: "#4BA5EB" }} /><Typography sx={{ mt: 2, color: "rgba(255,255,255,0.7)" }}>Opening…</Typography></>);
};

/**
 * Rendered by App.tsx instead of the normal routes when this popup comes back to Ocesha
 * (e.g. /success?session_id=… or /codegenie/generated-blogs?linkedin=connected).
 * It hands that URL to the SHOPLINE iframe and closes itself.
 */
export const ShoplinePopupReturn: React.FC<{ relay: string; path: string }> = ({ relay, path }) => {
  const [state, setState] = useState<"sending" | "done" | "failed">("sending");

  useEffect(() => {
    completePopupRelay(relay, path)
      .then(() => {
        setState("done");
        setTimeout(() => { try { window.close(); } catch { /* ignore */ } }, 600);
      })
      .catch(() => setState("failed"));
  }, [relay, path]);

  return shell(
    state === "sending"
      ? <><CircularProgress size={26} sx={{ color: "#4BA5EB" }} /><Typography sx={{ mt: 2 }}>Finishing…</Typography></>
      : state === "done"
      ? <><Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>All done</Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>You can close this window and go back to SHOPLINE.</Typography></>
      : <Typography>We couldn't send this back to SHOPLINE. Close this window and try again.</Typography>
  );
};

export default ShoplinePopup;
